import React, { useState, useCallback, useMemo, useEffect, useLayoutEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import MenuIcon from "@material-ui/icons/Menu";
import CloseIcon from "@material-ui/icons/Close";
import HelpIcon from "@material-ui/icons/Help";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
  useMediaQuery,
  useTheme,
  Box
} from "@mui/material";
import { menuConfig, getAccessibleMenu } from "../../config/menuConfig";
import { logout } from "../../action/auth";
import customConfig from "../../config";
import UserProfileMenu from "./UserProfileMenu";
import { useSidebarContext } from "../../context/SidebarContext";
import { getPrimaryThemeColor, getSecondaryThemeColor } from "../../utils/getThemeColor";
import "./LeftNavResponsive.css";

const LeftNavResponsive = ({ auth, tenantInfo, logout }) => {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // < 900px
  const { updateSidebarState } = useSidebarContext();
  
  // Get theme class names and colors from tenant info
  const themeClass = useMemo(() => {
    const primary = tenantInfo?.primaryColor || 'dark-blue';
    const secondary = tenantInfo?.secondaryColor || 'lime';
    return `${primary}-theme ${secondary}-theme-secondary`;
  }, [tenantInfo]);
  
  const primaryColor = useMemo(() => getPrimaryThemeColor(tenantInfo), [tenantInfo]);
  const secondaryColor = useMemo(() => getSecondaryThemeColor(tenantInfo), [tenantInfo]);
  const handleLogout = useCallback(() => {
    logout();
  }, [logout]);
  const [isExpanded, setIsExpanded] = useState(false); // DEFAULT: collapsed (icon-only)
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    devices: false,
    manufacturing: false,
    users: false,
    statistics: false,
    others: false
  });
  const [isHoverExpanded, setIsHoverExpanded] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const hoverTimeoutRef = React.useRef(null);

  // Sync sidebar state to context before paint so content area padding/margin match on first load
  useLayoutEffect(() => {
    updateSidebarState(isExpanded, isHoverExpanded);
  }, [isExpanded, isHoverExpanded, updateSidebarState]);

  // Cleanup hover timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // Build user context for permission checks
  const userContext = useMemo(
    () => ({
      userLevel: auth?.userLevel || 0,
      roles: auth?.roles || [],
      tenantId: tenantInfo?.tenantId || ""
    }),
    [auth, tenantInfo]
  );

  // Get accessible menu items for current user
  const accessibleMenu = useMemo(
    () => getAccessibleMenu(menuConfig, userContext),
    [userContext]
  );

  // Toggle section expansion with accordion behavior
  const toggleSection = useCallback((sectionId) => {
    setExpandedSections(prev => {
      // If opening a section, close all others (accordion behavior)
      if (!prev[sectionId]) {
        const newSections = {};
        Object.keys(prev).forEach(key => {
          newSections[key] = key === sectionId;
        });
        return newSections;
      }
      // If closing the current section
      return {
        ...prev,
        [sectionId]: false
      };
    });
  }, []);

  // Expand section on hover only (doesn't close on hover)
  const expandSectionOnHover = useCallback((sectionId) => {
    setExpandedSections(prev => {
      // Only expand if not already expanded
      if (prev[sectionId]) {
        return prev; // Don't toggle if already open
      }
      // Expand this section and close all others
      const newSections = {};
      Object.keys(prev).forEach(key => {
        newSections[key] = key === sectionId;
      });
      return newSections;
    });
  }, []);

  // Check if route is active
  const isActive = useCallback((path) => {
    if (!path) return false;
    return location.pathname.startsWith(path);
  }, [location.pathname]);

  // Check if section has active child
  const hasActiveChild = useCallback((section) => {
    if (!section.subitems) return false;
    return section.subitems.some(item => isActive(item.path));
  }, [isActive]);

  // Close mobile menu on link click - with proper delay for navigation
  const handleLinkClick = useCallback((e) => {
    if (isMobile) {
      // Don't close immediately - let navigation happen first
      setTimeout(() => {
        setIsMobileOpen(false);
      }, 300);
    }
  }, [isMobile]);

  // Render a menu item (subitem)
  const renderMenuItem = (item, isSubitem = true) => {
    const Icon = item.icon;
    const active = isActive(item.path);

    const expanded = isExpanded || isHoverExpanded || isMobileOpen;
    return (
      <ListItem key={item.id} disablePadding sx={{ display: 'block' }}>
        <ListItemButton
          className={isSubitem ? 'sidebar-subitem-button' : ''}
          component={Link}
          to={item.path}
          onClick={handleLinkClick}
          selected={active}
          sx={{
            minHeight: 40,
            justifyContent: expanded ? 'flex-start' : 'center',
            ...(expanded
              ? { px: isSubitem ? 2.5 : 1, py: 1.25 }
              : { px: 1.5, py: 0.75 }),
            borderRadius: 1,
            mx: expanded ? 1 : 0,
            mb: 0.5,
            boxSizing: 'border-box',
            backgroundColor: active ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
            border: active ? '1px solid rgba(255, 255, 255, 0.3)' : '1px solid transparent',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.12)',
              borderColor: 'rgba(255, 255, 255, 0.3)',
            },
            '&.Mui-selected': {
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
              }
            }
          }}
        >
          <ListItemIcon
            className={isSubitem ? 'sidebar-subitem-icon' : 'sidebar-section-icon'}
            sx={{
              minWidth: 0,
              mr: isExpanded || isHoverExpanded || isMobileOpen ? 1.5 : 0,
              justifyContent: 'center',
              color: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              width: isExpanded || isHoverExpanded || isMobileOpen ? 'auto' : '48px',
              flexShrink: 0,
              '& svg': {
                width: isSubitem ? '22px' : '24px',
                height: isSubitem ? '22px' : '24px',
              }
            }}
          >
            {Icon && <Icon style={{ fontSize: isSubitem ? 22 : 24 }} />}
          </ListItemIcon>
          <ListItemText
            primary={item.name}
            sx={{
              opacity: isExpanded || isHoverExpanded || isMobileOpen ? 1 : 0,
              visibility: isExpanded || isHoverExpanded || isMobileOpen ? 'visible' : 'hidden',
              width: isExpanded || isHoverExpanded || isMobileOpen ? 'auto' : 0,
              minWidth: 0,
              overflow: 'hidden',
              color: '#ffffff',
              m: 0,
              transition: 'opacity 0.2s ease 0.1s, visibility 0s ease 0.1s',
              '& .MuiListItemText-primary': {
                fontSize: isSubitem ? 14 : 16,
                fontWeight: active ? 500 : 400,
                whiteSpace: 'nowrap',
                lineHeight: 1.3,
              }
            }}
          />
        </ListItemButton>
      </ListItem>
    );
  };

  // Render a section with subitems
  const renderSection = (section) => {
    const Icon = section.icon;
    const isOpen = expandedSections[section.id];
    const hasActive = hasActiveChild(section);

    return (
      <Box key={section.id} sx={{ mb: 0.5 }}>
        {/* Section header / toggle */}
        <ListItem disablePadding sx={{ display: 'block' }}>
          <ListItemButton
            onClick={() => toggleSection(section.id)}
            onMouseEnter={() => {
              if (!isMobile) expandSectionOnHover(section.id);
            }}
            selected={hasActive}
            sx={{
              minHeight: 40,
              justifyContent: isExpanded || isHoverExpanded || isMobileOpen ? 'flex-start' : 'center',
              px: isExpanded || isHoverExpanded || isMobileOpen ? 1.75 : 1.5,
              py: 0.75,
              borderRadius: 1,
              ...(isExpanded || isHoverExpanded || isMobileOpen
                ? { mx: 1 }
                : { mx: 0 }),
              boxSizing: 'border-box',
              backgroundColor: hasActive ? 'rgba(255, 255, 255, 0.08)' : 'transparent',
              border: '1px solid transparent',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.12)',
                borderColor: 'rgba(255, 255, 255, 0.2)',
              },
              '&.Mui-selected': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                }
              }
            }}
          >
            <ListItemIcon
              className="sidebar-section-icon"
              sx={{
                minWidth: 0,
                mr: isExpanded || isHoverExpanded || isMobileOpen ? 1.5 : 0,
                justifyContent: 'center',
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                width: isExpanded || isHoverExpanded || isMobileOpen ? 'auto' : '48px',
                flexShrink: 0,
                '& svg': {
                    width: '24px',
                    height: '24px',
                }
              }}
            >
                {Icon && <Icon style={{ fontSize: 24 }} />}
            </ListItemIcon>
            <ListItemText
              primary={section.name}
              sx={{
                opacity: isExpanded || isHoverExpanded || isMobileOpen ? 1 : 0,
                visibility: isExpanded || isHoverExpanded || isMobileOpen ? 'visible' : 'hidden',
                width: isExpanded || isHoverExpanded || isMobileOpen ? 'auto' : 0,
                overflow: 'hidden',
                color: '#ffffff',
                flex: 1,
                m: 0,
                transition: 'opacity 0.2s ease 0.1s, visibility 0s ease 0.1s',
                '& .MuiListItemText-primary': {
                  fontSize: 16,
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  lineHeight: 1.3,
                }
              }}
            />
            {section.subitems && section.subitems.length > 0 && (
              <ExpandMoreIcon
                style={{
                  color: '#ffffff',
                  fontSize: '18px',
                  transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  display: isExpanded || isHoverExpanded || isMobileOpen ? 'block' : 'none',
                  flexShrink: 0,
                  marginLeft: '8px',
                }}
              />
            )}
          </ListItemButton>
        </ListItem>

        {/* Subitems with Collapse - use numeric timeout to avoid "Timeout" runtime error (auto can fail in some cases) */}
        {section.subitems && section.subitems.length > 0 && (
          <Collapse in={isOpen} timeout={300} unmountOnExit={false}>
            <List component="div" disablePadding className="sidebar-subitem-list" sx={{ pl: 0 }}>
              {section.subitems.map(item => renderMenuItem(item, true))}
            </List>
          </Collapse>
        )}
      </Box>
    );
  };

  return (
    <>
      {/* Mobile toggle button - LEFT SIDE */}
      {isMobile && (
        <IconButton
          onClick={() => setIsMobileOpen(!isMobileOpen)}
          sx={{
            position: 'fixed !important',
            top: '16px !important',
            left: '16px !important',
            zIndex: '9999 !important',
            backgroundColor: 'rgba(255, 255, 255, 0.95) !important',
            border: 'none !important',
            color: `${primaryColor} !important`,
            borderRadius: '8px !important',
            width: '44px',
            height: '44px',
            display: 'flex !important',
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover': {
              backgroundColor: '#ffffff !important',
            },
            boxShadow: '0 2px 8px rgba(0,0,0,0.2) !important',
            '@media (min-width: 900px)': {
              display: 'none !important',
            }
          }}
          aria-label="Toggle menu"
        >
          {isMobileOpen ? <CloseIcon style={{ fontSize: '22px' }} /> : <MenuIcon style={{ fontSize: '22px' }} />}
        </IconButton>
      )}
      {/* MUI Drawer for Sidebar */}
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? isMobileOpen : true}
        onClose={() => setIsMobileOpen(false)}
        ModalProps={{
          keepMounted: true, // Better mobile performance
        }}
        className={`${themeClass} left-navbar`}
        slotProps={{
          paper: {
            className: `${themeClass} left-navbar ${(isExpanded || isHoverExpanded || isMobileOpen) ? 'left-navbar-expanded' : 'left-navbar-collapsed'}`,
            style: {
              backgroundColor: primaryColor,
              borderRight: '1px solid rgba(255, 255, 255, 0.1)',
            },
            sx: {
              backgroundColor: `${primaryColor} !important`,
              borderRight: '1px solid rgba(255, 255, 255, 0.1) !important',
              // Ensure all text and icons are white, but exclude avatar text
              '& *:not(.MuiAvatar-root):not(.MuiAvatar-root *)': {
                color: '#ffffff !important',
              }
            },
            onMouseEnter: () => {
              if (!isMobile) {
                if (hoverTimeoutRef.current) {
                  clearTimeout(hoverTimeoutRef.current);
                }
                setIsHoverExpanded(true);
              }
            },
            onMouseLeave: () => {
              if (!isMobile) {
                if (hoverTimeoutRef.current) {
                  clearTimeout(hoverTimeoutRef.current);
                }
                hoverTimeoutRef.current = setTimeout(() => {
                  if (!isProfileMenuOpen) {
                  setIsHoverExpanded(false);
                  }
                }, 100);
              }
            },
          },
        }}
        sx={{
          width: isMobile ? 320 : (isExpanded || isHoverExpanded ? 320 : 72),
          flexShrink: 0,
          position: 'relative',
          '& .MuiDrawer-paper': {
            width: isMobile ? 320 : (isExpanded || isHoverExpanded ? 320 : 72),
            boxSizing: 'border-box',
            backgroundColor: `${primaryColor} !important`,
            borderRight: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: isMobile ? 0 : '0 16px 0 0',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.easeInOut,
              duration: 300,
            }),
            overflowX: 'hidden',
            overflowY: 'hidden',
            boxShadow: '2px 0 12px rgba(0, 0, 0, 0.3)',
            position: 'fixed',
            left: 0,
            top: 0,
            height: '100vh',
            maxHeight: '100vh',
            zIndex: isMobile ? 1300 : 998,
            transform: 'translateX(0)',
            display: 'flex',
            flexDirection: 'column',
            padding: 0,
          },
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          }
        }}
      >
        {/* Desktop toggle button */}
        {!isMobile && (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2, pb: 1.5, flexShrink: 0 }}>
            <IconButton
              onClick={() => setIsExpanded(!isExpanded)}
              sx={{
                width: 40,
                height: 40,
                backgroundColor: 'transparent',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                color: '#ffffff',
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderColor: '#ffffff',
                }
              }}
              title={isExpanded ? "Collapse" : "Expand"}
              aria-label="Toggle sidebar"
            >
              <MenuIcon style={{ fontSize: '20px' }} />
            </IconButton>
          </Box>
        )}

        {/* Menu container - scrollable area */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            px: 0.5,
            pt: isMobile ? 8 : 0,
            pb: 1,
            minHeight: 0,
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: 'rgba(255, 255, 255, 0.4)',
            },
          }}
        >
          <List sx={{ pt: 0 }}>
            {accessibleMenu.length > 0 ? (
              accessibleMenu.map(section => renderSection(section))
            ) : (
              <Box sx={{ p: 2.5, textAlign: 'center', color: '#ffffff', fontSize: 12 }}>
                No menu items available
              </Box>
            )}
          </List>
        </Box>

        {/* Footer: single row, profile left, ? and Logout right, all vertically aligned */}
        <Box
          sx={{
            flexShrink: 0,
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 0.75,
            px: 0.5,
            minHeight: 48,
          }}
        >
          <UserProfileMenu
            logout={handleLogout}
            username={auth?.username || auth?.email || 'User'}
            themeClass={themeClass}
            primaryColor={primaryColor}
          />
          {(isExpanded || isHoverExpanded || isMobileOpen) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <IconButton
                onClick={() => window.open(customConfig.supportURL, '_blank', 'noopener, noreferrer')}
                sx={{ color: '#ffffff', p: 1, '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' } }}
                aria-label="Support"
                title="Support"
              >
                <HelpIcon style={{ fontSize: 22 }} />
              </IconButton>
              <IconButton
                onClick={handleLogout}
                sx={{ color: '#ffffff', p: 1, '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' } }}
                aria-label="Logout"
                title="Logout"
              >
                <ExitToAppIcon style={{ fontSize: 22 }} />
              </IconButton>
            </Box>
          )}
        </Box>
      </Drawer>
    </>
  );
};

const mapStateToProps = (state) => ({
  auth: state.auth,
  tenantInfo: state.tenant?.tenantInfo || {}
});

export default connect(mapStateToProps, { logout })(LeftNavResponsive);
